import React, { useMemo, useState } from "react";
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import {
  Checkbox,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Typography,
} from "@material-ui/core";
import {
  DenseDivider,
  DenseIcon,
  DenseMenu,
  PromptDialog,
  SlimScrollbar,
  TextDialog
} from "../components";
import AddIcon from '@material-ui/icons/Add';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { BookmarkItem } from ".";
import BookmarksIcon from '@material-ui/icons/Bookmarks';
import DeleteIcon from "@material-ui/icons/Delete";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import {
  createBookmark,
  updateBookmark,
  deleteBookmark
} from "../redux/bookmarks-slice";
import { store } from "../redux/store";
import { toast } from "../app";
import LocalScene from "../viewer/js/projections";
import { useAuth, useBookmarks, useContextMenu, useDialog, useViewer } from "../hooks";
import { AerialViewState, SceneCameraState } from "../redux/camera-slice";
import { SettingsState } from "../redux/settings-slice";
import { getVisibleAssets, selectAllAssets } from "../redux/assets-slice";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addItem: {
      paddingTop: "0px",
      paddingBottom: "0px"
    },
    footerDiv: {
      width: "100%",
      boxShadow: "0px 10px 10px 2px",
      zIndex: 1,
      textAlign: "center"
    },
    footerCheckbox: {
      padding: theme.spacing(0.5),
      marginRight: theme.spacing(0.5)
    }
  })
);

export const getStoredState = () => {
  const state = store.getState();

  const settings = {...state.settings} as SettingsState;
  const aerialState = state.camera.aerialState as AerialViewState;
  const sceneState = state.camera.sceneState as SceneCameraState;

  return {settings, aerialState, sceneState};
};

export const BookmarkList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {viewer} = useViewer();
  const {permissions} = useAuth();
  const {t} = useTranslation();
  const contextMenu = useContextMenu();
  const bookmarkDialog = useDialog();
  const deleteDialog = useDialog();
  const {bookmarks} = useBookmarks();

  const assets = useSelector(selectAllAssets);

  const visibleAssets = useMemo(() => {
    return getVisibleAssets(assets);
  }, [assets]);

  const [expanded, setExpanded] = useState(true);

  const handleSubmit = (name: string) => {
    bookmarkDialog.handleClose();

    if (!LocalScene.initialized) {
      toast.warning(t('bookmarks.invalid_position'));
      return;
    }

    const position = viewer.getCameraPosition().toArray();
    const {settings, aerialState, sceneState} = getStoredState();
    const visibleAssetIDs = visibleAssets.map(asset => asset.id);

    dispatch(createBookmark({
      name,
      position,
      settings,
      visibleAssets: visibleAssetIDs,
      aerialState,
      sceneState
    }));
  };

  const handleClick = (event) => {
    const isCheckBox = (event.target.type === "checkbox");
    if (isCheckBox) return;

    setExpanded(!expanded);
  };

  const toggleCheckboxes = () => {
    let newVisibility;
    if (numVisible > 0 && numHidden > 0) {
      newVisibility = true;
    } else if (numVisible > 0) {
      newVisibility = false;
    } else {
      newVisibility = true;
    }

    for (let bookmark of bookmarks) {
      dispatch(updateBookmark({
        bookmarkID: bookmark.id,
        visible: newVisibility
      }));
    }
  };

  const handleDeleteSubmit = () => {
    for (let bookmark of bookmarks) {
      dispatch(deleteBookmark(bookmark.id));
    }
    deleteDialog.handleClose();
  };

  const handleDeleteClick = () => {
    deleteDialog.handleOpen();
    contextMenu.handleClose();
  };

  const numVisible = bookmarks.filter(x => x.visible).length;
  const numHidden = bookmarks.filter(x => !x.visible).length;

  return (
    <React.Fragment>

      {/* Add item button */}
      {permissions.create && (<React.Fragment>
        <ListItem
          button
          onClick={bookmarkDialog.handleOpen}
          className={classes.addItem}
        >
          <DenseIcon>
            <AddIcon fontSize="small"/>
          </DenseIcon>
          <ListItemText secondary={t('bookmarks.add_bookmark')}/>
        </ListItem>

        <DenseDivider variant="middle" spacing={10}/>
      </React.Fragment>)}

      {/* Bookmarks list */}
      <SlimScrollbar>
        {(bookmarks.length > 0) && (<React.Fragment>
          <List component="div" disablePadding>
            <ListItem
              button
              dense
              onClick={handleClick}
              onContextMenu={contextMenu.handleOpen}
            >
              <DenseIcon>
                <BookmarksIcon fontSize="small"/>
              </DenseIcon>
              <ListItemText primary={t('bookmarks.bookmarks')}/>

              {(bookmarks.length > 0) && (<React.Fragment>
                {/* Show / hide contents */}
                {expanded ? <ExpandLess/> : <ExpandMore/>}

                {/* Toggle all folder assets */}
                <Checkbox
                  edge="end"
                  size="small"
                  checked={numVisible > 0}
                  onChange={toggleCheckboxes}
                  indeterminate={numVisible > 0 && numHidden > 0}
                />
              </React.Fragment>)}

            </ListItem>

            {expanded && (<React.Fragment>
              <List component="div" disablePadding dense>
                {bookmarks.map(bookmark =>
                  <BookmarkItem
                    key={bookmark.id}
                    bookmark={bookmark}
                  />
                )}
              </List>
            </React.Fragment>)}

          </List>
        </React.Fragment>)}
      </SlimScrollbar>

      <DenseMenu
        open={contextMenu.open}
        onClose={contextMenu.handleClose}
        anchorPosition={contextMenu.anchorPosition}
      >
        <MenuItem
          onClick={handleDeleteClick}
          disabled={!permissions.delete}
        >
          <DenseIcon>
            <DeleteIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t('buttons.delete')}</Typography>
        </MenuItem>
      </DenseMenu>

      <TextDialog
        clear
        open={bookmarkDialog.open}
        onCancel={bookmarkDialog.handleClose}
        onSubmit={handleSubmit}
        title={t('bookmarks.create_new_bookmark')}
        prompt={t('bookmarks.enter_new_bookmark_name')}
        label={t('bookmarks.bookmark_name')}
      />

      <PromptDialog
        open={deleteDialog.open}
        onCancel={deleteDialog.handleClose}
        onSubmit={handleDeleteSubmit}
        title={t('bookmarks.delete_bookmarks')}
        prompt={t('bookmarks.do_you_want_to_delete_all_bookmarks')}
        button={t("buttons.delete")}
      />

    </React.Fragment>
  );
};
